import { Typography } from '@material-ui/core';
import {
  ColorDynamic,
  Inline,
  Stack,
  useResponsiveValue,
} from '@superdispatch/ui';
import {
  DropEvent,
  ErrorCode,
  FileError,
  FileRejection,
  useDropzone,
} from 'react-dropzone';
import { LoadDropIcon } from 'shared/icons/LoadsDropIcon';
import styled from 'styled-components';
import { WarningRounded } from '@material-ui/icons';
import {
  LoadsImportSnackbar,
  acceptedFilesAtom,
  rejectedFilesAtom,
} from '../loads-page/core/LoadsImportSnackbar';
import { useAtom } from 'jotai';
import { useQueryClient } from '@tanstack/react-query';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import { usePermissionAccess } from 'shared/modules/permission/data/usePermissionAccess';
import { UpgradePlanDialog } from './UpgradePlanDialog';
import { useState, useRef, useEffect } from 'react';
import { trackLoadsEvent } from '../data/LoadsAnalytics';
import draggingImage from '../assets/drag-image.png';

const Container = styled.div<{ isMobile: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  padding: 16px;
  display: flex;
  align-items: center;
  z-index: 1500;
  bottom: ${({ isMobile }) => (isMobile ? '50px' : '0')};
  left: ${({ isMobile }) => (isMobile ? '0' : '85px')};
`;

const DropzoneAccept = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  border-radius: 8px;
  position: relative;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  backdrop-filter: blur(8px);
  background: ${ColorDynamic.White50};
  border: 4px dashed ${ColorDynamic.Blue300};
`;

const WarningText = styled(Typography)`
  color: ${ColorDynamic.Yellow300};
`;

const AnimatedImage = styled.img`
  top: 200px;
  left: 600px;
  width: 80px;
  height: 80px;
  z-index: 2000;
  position: fixed;
`;

const MAX_IMPORT_FILE_SIZE_BYTES = 20 * 1024 * 1024;

const IMPORT_FILE_MIME_TYPES = new Set([
  'text/csv',
  'application/pdf',
  'application/vnd.ms-excel',
]);

function hasAllowedImportFileExtension(fileName: string) {
  const lowerName = fileName.toLowerCase();

  return lowerName.endsWith('.pdf') || lowerName.endsWith('.csv');
}

export function validateLoadsImportFile(file: File): FileError | null {
  const isAllowedType =
    IMPORT_FILE_MIME_TYPES.has(file.type) ||
    hasAllowedImportFileExtension(file.name);

  if (!isAllowedType) {
    return {
      code: ErrorCode.FileInvalidType,
      message: 'Only PDF and CSV files are allowed.',
    };
  }

  if (file.size > MAX_IMPORT_FILE_SIZE_BYTES) {
    return {
      code: ErrorCode.FileTooLarge,
      message: 'File size should be less than 20 MB.',
    };
  }

  return null;
}

const handledDropEvents = new WeakSet<Event>();
export function claimLoadsImportDropEvent(event: DropEvent): boolean {
  const nativeEvent = 'nativeEvent' in event ? event.nativeEvent : event;

  if (handledDropEvents.has(nativeEvent)) {
    return false;
  }

  handledDropEvents.add(nativeEvent);

  return true;
}

export function LoadsImportZone({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const isMobile = useResponsiveValue(true, true, false);
  const [acceptedFiles, setFiles] = useAtom(acceptedFilesAtom);
  const [fileRejections, setRejectedFiles] = useAtom(rejectedFilesAtom);
  const [modalState, setModalState] = useState<{ type: string } | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);
  const { data: settings } = useCarrierSettings();
  const canNotImportLoads = usePermissionAccess({
    name: 'loads',
    permission: 'can_import',
  });

  const { getRootProps, getInputProps, isDragActive, isDragReject } =
    useDropzone({
      onDrop: (
        files: File[],
        rejectedFiles: FileRejection[],
        event: DropEvent,
      ) => {
        if (!claimLoadsImportDropEvent(event)) {
          return;
        }

        if (settings?.can_import_load) {
          setFiles(files);
          setIsAnimating(true);
          setRejectedFiles(rejectedFiles);
          queryClient.removeQueries({ queryKey: ['loads-import'] });
          trackLoadsEvent({
            name: 'Carrier Imported Load',
            utm_medium: 'drag&drop',
          });
        } else {
          setModalState({ type: 'upgrade_plan' });
        }
      },
      validator: validateLoadsImportFile,
      noClick: true,
      multiple: true,
      noKeyboard: true,
      disabled: canNotImportLoads,
    });

  useEffect(() => {
    let animation: Animation | undefined;

    if (isAnimating && imageRef.current) {
      const targetY = window.innerHeight - 120;

      animation = imageRef.current.animate(
        [
          { left: '350px', top: '350px', transform: 'scale(1)', opacity: 1 },
          {
            left: '200px',
            top: '400px',
            transform: 'scale(1)',
            opacity: 1,
            offset: 0.3,
          },
          {
            left: '100px',
            top: `${targetY}px`,
            transform: 'scale(0.5)',
            opacity: 0,
          },
        ],
        {
          duration: 1350,
          easing: 'cubic-bezier(0.4, 0.0, 0.2, 1)',
          fill: 'forwards',
        },
      );

      animation.onfinish = () => {
        setIsAnimating(false);
      };
    }

    return () => {
      animation?.cancel();
    };
  }, [isAnimating]);

  useEffect(() => {
    return () => {
      setFiles([]);
      setRejectedFiles([]);
    };
  }, [setFiles, setRejectedFiles]);

  return (
    <div {...getRootProps()}>
      <input {...getInputProps()} />
      {children}
      {isDragActive && (
        <Container isMobile={isMobile}>
          <DropzoneAccept>
            <Stack space="large" align="center">
              <LoadDropIcon />

              <Stack align="center" space="small">
                <Typography variant="h2" color="primary">
                  Drop PDF or CSV to Import Loads
                </Typography>
                {isDragReject && (
                  <Inline verticalAlign="center" space="xsmall">
                    <WarningRounded
                      fontSize="medium"
                      htmlColor={ColorDynamic.Yellow300}
                    />
                    <WarningText variant="h3">
                      Some files have unsupported format and will be ignored
                    </WarningText>
                  </Inline>
                )}
              </Stack>
            </Stack>
          </DropzoneAccept>
        </Container>
      )}
      {isAnimating && (
        <AnimatedImage
          ref={imageRef}
          src={draggingImage}
          alt="Drag animation"
        />
      )}
      <LoadsImportSnackbar
        files={acceptedFiles}
        rejectedFiles={fileRejections}
        onClose={() => {
          setFiles([]);
          setRejectedFiles([]);
        }}
        onRemoveRejectedFile={(file) => {
          setRejectedFiles((prev) =>
            prev.filter((f) => f.file.name !== file.name),
          );
        }}
      />
      <UpgradePlanDialog
        onClose={() => {
          setModalState(null);
        }}
        type="load_import"
        open={modalState?.type === 'upgrade_plan'}
        source="Load List"
      />
    </div>
  );
}
